import { now } from "@/helpers/time";
import {
  Box,
  Button,
  Circle,
  Grid,
  HStack,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import { LuArrowLeft, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import {
  DEADLINES,
  PALOMMA_PAYMENTS,
  datedDeadlines,
  palommaPaymentDate,
} from "./deadlines";

const WEEKDAYS = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

// helpers/time only loads holidays for the previous, current and next year.
const HOLIDAY_YEARS = { first: now().year() - 1, last: now().year() + 1 };

type CalendarEvent = {
  date: Dayjs;
  title: string;
  caption: string;
  isPalomma: boolean;
};

const monthEvents = (month: Dayjs): CalendarEvent[] => {
  const paymentDate = palommaPaymentDate(month);

  return [
    ...datedDeadlines(month).map((deadline) => ({
      date: deadline.date,
      title: deadline.title,
      caption: deadline.caption,
      isPalomma: false,
    })),
    ...PALOMMA_PAYMENTS.map((payment) => ({
      date: paymentDate,
      title: "Pago de obligaciones",
      caption: payment.caption,
      isPalomma: true,
    })),
  ];
};

// Monday-first grid, padded with empty cells to complete the weeks.
const monthCells = (month: Dayjs): (Dayjs | undefined)[] => {
  const start = month.startOf("month");
  const leading = (start.day() + 6) % 7;
  const days = Array.from({ length: start.daysInMonth() }, (_, index) =>
    start.add(index, "day")
  );
  const trailing = (7 - ((leading + days.length) % 7)) % 7;

  return [
    ...Array<undefined>(leading).fill(undefined),
    ...days,
    ...Array<undefined>(trailing).fill(undefined),
  ];
};

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <HStack gap={2}>
    <Box boxSize="3" borderRadius="sm" bg={color} />
    <Text color="gray.600">{label}</Text>
  </HStack>
);

const EventChip = ({ event }: { event: CalendarEvent }): JSX.Element => {
  const palette = event.isPalomma ? "green" : "palomma";

  return (
    <Stack
      gap={0}
      px={2}
      py={1.5}
      borderRadius="md"
      borderWidth="1px"
      borderColor={`${palette}.200`}
      bg={`${palette}.50`}
    >
      <Text
        fontSize="xs"
        fontWeight={700}
        lineHeight="shorter"
        color={`${palette}.700`}
      >
        {event.title}
      </Text>
      <Text
        display={{ base: "none", xl: "block" }}
        fontSize="11px"
        lineHeight="shorter"
        color="gray.600"
      >
        {event.caption}
      </Text>
    </Stack>
  );
};

type Props = { today: Dayjs; onBack: () => void };

const KeyDatesCalendar = ({ today, onBack }: Props): JSX.Element => {
  const [monthOffset, setMonthOffset] = useState(0);
  const month = today.add(monthOffset, "month");
  const events = monthEvents(month);
  const isFirstMonth =
    month.year() === HOLIDAY_YEARS.first && month.month() === 0;
  const isLastMonth =
    month.year() === HOLIDAY_YEARS.last && month.month() === 11;

  return (
    <Stack gap={5} flex="1" minH={0} overflowY="auto">
      <Box>
        <Button variant="plain" colorPalette="palomma" px={0} onClick={onBack}>
          <LuArrowLeft />
          Fechas clave
        </Button>
      </Box>

      <HStack justify="space-between" flexWrap="wrap" gap={4} flexShrink={0}>
        <HStack gap={3}>
          <Text fontSize="2xl" fontWeight={700} textTransform="capitalize">
            {month.format("MMMM YYYY")}
          </Text>
          <IconButton
            aria-label="Mes anterior"
            variant="outline"
            size="xs"
            disabled={isFirstMonth}
            onClick={() => setMonthOffset((offset) => offset - 1)}
          >
            <LuChevronLeft />
          </IconButton>
          <IconButton
            aria-label="Mes siguiente"
            variant="outline"
            size="xs"
            disabled={isLastMonth}
            onClick={() => setMonthOffset((offset) => offset + 1)}
          >
            <LuChevronRight />
          </IconButton>
        </HStack>
        <HStack gap={5}>
          <LegendItem
            color="palomma.600"
            label={`Tú haces · ${DEADLINES.length} fechas`}
          />
          <LegendItem
            color="green.500"
            label={`Palomma hace · ${PALOMMA_PAYMENTS.length} fechas`}
          />
        </HStack>
      </HStack>

      <Grid
        templateColumns="repeat(7, minmax(0, 1fr))"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        overflow="hidden"
        bg="white"
        flexShrink={0}
      >
        {WEEKDAYS.map((weekday) => (
          <Text
            key={weekday}
            px={3}
            py={2}
            fontSize="xs"
            fontFamily="mono"
            letterSpacing="wider"
            textTransform="uppercase"
            color="gray.600"
            borderBottomWidth="1px"
            borderColor="gray.200"
          >
            {weekday}
          </Text>
        ))}
        {monthCells(month).map((day, index) => {
          const isToday = day?.isSame(today, "day");

          return (
            <Stack
              key={index}
              gap={2}
              p={2.5}
              minH="28"
              bg={day ? undefined : "gray.50"}
              borderColor="gray.200"
              borderTopWidth={index < 7 ? 0 : "1px"}
              borderLeftWidth={index % 7 === 0 ? 0 : "1px"}
            >
              {day && (
                <>
                  {isToday ? (
                    <HStack gap={2}>
                      <Circle
                        size="7"
                        bg="gray.900"
                        color="white"
                        fontSize="sm"
                        fontWeight={700}
                      >
                        {day.date()}
                      </Circle>
                      <Text fontSize="2xs" fontFamily="mono" fontWeight={600}>
                        HOY
                      </Text>
                    </HStack>
                  ) : (
                    <Text fontWeight={600} color="gray.700">
                      {day.date()}
                    </Text>
                  )}
                  {events
                    .filter((event) => event.date.isSame(day, "day"))
                    .map((event) => (
                      <EventChip key={event.caption} event={event} />
                    ))}
                </>
              )}
            </Stack>
          );
        })}
      </Grid>

      <Text color="gray.600" fontSize="sm" flexShrink={0}>
        Cuando un corte cae sábado, domingo o festivo, se corre al siguiente día
        hábil. La fecha efectiva es la que aparece en cada tarjeta.
      </Text>
    </Stack>
  );
};

export default KeyDatesCalendar;
